import { usePageTitle } from '../../hooks/usePageTitle';
import GuideSidebar from '../../components/guides/GuideSidebar';
import GuideAccordion from '../../components/guides/GuideAccordion';
import ComparisonTable from '../../components/guides/ComparisonTable';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';

export default function HullDesign() {
  usePageTitle('Hull & Design Guide | Monohull vs Catamaran');

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
        <div className="lg:col-span-1"><GuideSidebar /></div>
        <div className="lg:col-span-3">
          <h1 className="text-4xl md:text-5xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Hull & Design</h1>
          <p className="text-lg text-white/70 mb-8">Hull design fundamentally affects performance, comfort, and suitability for Southeast Asian cruising grounds.</p>

          <div className="space-y-2">
            <GuideAccordion title="Monohull Yachts" defaultOpen={true}>
              <p>Traditional single-hull design. Deeper draft, more efficient sailing, classic yachting feel.</p>
              <ComparisonTable
                headers={['Aspect', 'Performance/Feature']}
                rows={[
                  ['Draft', '6-10 ft (requires deeper water)'],
                  ['Stability', 'Heels when sailing, rocks at anchor'],
                  ['Speed', 'Efficient in heavy winds'],
                  ['Interior', 'Narrower beam, vertical space'],
                  ['Anchoring', 'Better in deep harbors'],
                  ['Southeast Asia', 'Good for Phuket marinas, Singapore'],
                ]}
              />
            </GuideAccordion>

            <GuideAccordion title="Catamaran (Multihull)">
              <p>Two parallel hulls connected by bridge deck. Dominates Southeast Asia charter market due to shallow draft and stability.</p>
              <h4 className="text-gold font-semibold mt-4 mb-2">Catamaran Advantages in SE Asia:</h4>
              <ul className="space-y-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Shallow Draft (4-5 ft):</strong> Access hidden lagoons (Phang Nga Bay, Raja Ampat, Palawan)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Stability:</strong> Minimal rolling, ideal for families and seasickness prevention</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Space:</strong> Massive beam creates huge saloons and deck areas</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Dual Engines:</strong> Redundancy and excellent maneuverability</span></li>
              </ul>
              <div className="bg-white/5 p-4 mt-4 border-l-2 border-gold">
                <p className="text-white/90"><strong>Charter Market:</strong> 60% of luxury charters in Thailand/Indonesia use catamarans (Lagoon, Bali, Fountaine Pajot brands).</p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Trimaran (Three Hulls)">
              <p>Central hull with two smaller outriggers. Rare but offers catamaran stability with monohull sailing performance.</p>
              <ul className="space-y-2 mt-4">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Faster than catamarans under sail</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>More stable than monohulls</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Less interior space than catamarans</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Rare in Southeast Asia charter fleets</span></li>
              </ul>
            </GuideAccordion>

            <GuideAccordion title="Hull Materials">
              <p>Material choice affects durability, cost, and performance in tropical conditions.</p>
              <ComparisonTable
                headers={['Material', 'Pros', 'Cons', 'SE Asia Use']}
                rows={[
                  ['Fiberglass (GRP)', 'Low maintenance, smooth finish', 'Can blister in tropics', 'Most common (90%)'],
                  ['Aluminum', 'Lightweight, no corrosion', 'Expensive, heat absorption', 'Expedition yachts'],
                  ['Steel', 'Strong, cheap, easy repair', 'Heavy, rust risk', 'Expedition, phinisi'],
                  ['Wood (Teak)', 'Traditional, beautiful', 'High maintenance', 'Indonesian phinisi only'],
                  ['Carbon Fiber', 'Ultra-lightweight, strong', 'Very expensive', 'Racing yachts only'],
                ]}
              />
            </GuideAccordion>

            <GuideAccordion title="Displacement vs Planing Hulls">
              <p><strong>Displacement Hulls:</strong> Push through water, limited by waterline length. Most sailboats and trawlers. Efficient for long-distance cruising.</p>
              <p className="mt-3"><strong>Planing Hulls:</strong> Rise out of water at speed, glide on surface. Common in express cruisers and sportfish. Faster but thirstier.</p>
              <div className="bg-white/5 p-4 mt-4 border-l-2 border-gold">
                <p className="text-white/90"><strong>SE Asia Recommendation:</strong> Displacement or semi-displacement hulls better for extended charters with fuel stops 200+ miles apart.</p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Keel Types (Sailing Yachts)">
              <p>The keel provides stability and prevents sideways drift when sailing.</p>
              <ul className="space-y-2 mt-4">
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Fin Keel:</strong> Modern, deep, fast. Draft 6-8 ft. Best for open water.</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Wing Keel:</strong> Shorter draft (5-6 ft), less efficient but better for shallow SE Asia.</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Lifting Keel:</strong> Adjustable depth! Rare but perfect for exploring shallow Indonesian waters.</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span><strong>Centerboard:</strong> Retractable board in shallow-draft cruisers.</span></li>
              </ul>
            </GuideAccordion>
          </div>

          <div className="mt-12 flex gap-4">
            <Link to="/guides/types-purpose"><Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-[#0A192F]">← Types by Purpose</Button></Link>
            <Link to="/guides/buying-ownership"><Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">Next: Buying & Ownership →</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
